import { Suspense } from "react"
import { useCurrentLanguage } from "@/hooks/useCurrentLanguage"
import { convertLanguageToISOCode } from "@/lib/language-to-iso-code"
import { AttachmentList } from "@/modules/patients/components/attachments/attachment-list"
import { CallLineBanner } from "@/modules/patients/components/call-line-banner"
import { useAppointmentAttachments } from "@/modules/patients/detail/progress-note/hooks/useAppointmentAttachments"
import { usePatientFileContext } from "@/modules/patients/detail/store/usePatientStore"
import { useContextAppointment } from "@/modules/patients/hooks/useContextAppointment"
import { TelephoneLineType } from "@/modules/patients/types/ProgressNoteDocument"
import { RequestUploadFilesContainer } from "@/modules/waiting-room/components/request-upload-files-container"
import { AppointmentAttachmentFragment$key } from "@/modules/waiting-room/services/__generated__/AppointmentAttachmentFragment.graphql"

interface AttachmentsSectionProps {
  appointmentAttachments: AppointmentAttachmentFragment$key
  appointmentId: string
  telephoneLine: TelephoneLineType
  isReadOnlyProgressNote?: boolean
}
const AttachmentsSection = ({
  appointmentAttachments,
  appointmentId,
  telephoneLine,
  isReadOnlyProgressNote,
}: AttachmentsSectionProps) => {
  const patientFileInfo = usePatientFileContext((state) => state.patientFileInfo)
  const currentLanguage = useCurrentLanguage()
  const contextAppointment = useContextAppointment()
  const fileUploadRequestRef = contextAppointment?.fileUploadRequest

  const attachments = useAppointmentAttachments({ uploadSources: ["PATIENT", "SOFIA"], appointmentAttachments })

  const mapTelephoneLineContent = telephoneLine?.telephoneLineTranslations.find(
    (translation) => currentLanguage.toUpperCase() === convertLanguageToISOCode(translation.language),
  )?.content

  return (
    <>
      {attachments && attachments.length > 0 && (
        <AttachmentList mediaFiles={attachments} appointmentId={appointmentId} />
      )}
      {mapTelephoneLineContent && (
        <CallLineBanner bannerInfo={mapTelephoneLineContent} testId="progress-note-call-line-banner" />
      )}
      {!isReadOnlyProgressNote && (
        <Suspense fallback={null}>
          <RequestUploadFilesContainer
            isDisabled={contextAppointment?.status && ["CLOSED", "CANCELLED"].includes(contextAppointment.status)}
            appointmentId={appointmentId}
            contactChannel={patientFileInfo?.contactChannel}
            isContactChannelVerified={patientFileInfo?.isContactChannelVerified}
            fileUploadRequestRef={fileUploadRequestRef}
          />
        </Suspense>
      )}
    </>
  )
}

export { AttachmentsSection }
